import {
  AWS_API_URL,
  AWS_CLIENT_ID,
  AWS_COGNITO_DOMAIN,
  AWS_REDIRECT_URI,
  IS_MOBILE_APP
} from '@/constants'
import type { ResponseType } from './request'

export interface AWSTokenResponse {
  access_token: string
  expires_in: number
  id_token: string
  refresh_token: string
  token_type: string
}

export interface AuthChallengeResponse {
  type: 'challenge'
  challengeName: string
  session: string
  username: string
}

interface AuthSuccessResponse {
  type: 'authenticated'
  tokens: {
    accessToken: string
    idToken: string
    refreshToken: string
    expiresIn: number
  }
}

type BackendAuthResponse = AuthSuccessResponse | AuthChallengeResponse

export const loginWithPassword = async (
  email: string,
  password: string
): Promise<BackendAuthResponse> => {
  const response = await fetch(`${AWS_API_URL}/dashboard/v1/public/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password })
  })

  if (!response.ok) {
    const errorBody = await response.json()
    throw new Error(errorBody.message || 'Login failed')
  }

  const result = (await response.json()) as ResponseType<BackendAuthResponse>
  if (!result.success) throw new Error(result.message || 'Login failed')
  return result.data
}

export const exchangeCodeForToken = async (
  code: string
): Promise<AWSTokenResponse> => {
  const redirectUrl = IS_MOBILE_APP ? 'watchdog://home' : AWS_REDIRECT_URI
  const body = `grant_type=authorization_code&client_id=${AWS_CLIENT_ID}&code=${code}&redirect_uri=${redirectUrl}`
  const response = await fetch(`${AWS_COGNITO_DOMAIN}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body
  })
  return response.json()
}

export const refreshToken = async (
  refreshToken: string
): Promise<AWSTokenResponse> => {
  const body = `grant_type=refresh_token&client_id=${AWS_CLIENT_ID}&refresh_token=${refreshToken}`
  const response = await fetch(`${AWS_COGNITO_DOMAIN}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body
  })
  const newCreds: AWSTokenResponse = await response.json()
  return {
    ...newCreds,
    refresh_token: refreshToken // Keep the old refresh token
  }
}

export const invalidateToken = async (accessToken: string): Promise<void> => {
  await fetch(`${AWS_COGNITO_DOMAIN}/oauth2/revoke`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: `token=${accessToken}&client_id=${AWS_CLIENT_ID}`
  })
}

export const cognitoLogout = (): void => {
  window.location.href = `${AWS_COGNITO_DOMAIN}/logout?client_id=${AWS_CLIENT_ID}&logout_uri=${AWS_REDIRECT_URI}`
}

export const forgotPassword = async (email: string): Promise<void> => {
  const response = await fetch(
    `${AWS_API_URL}/dashboard/v1/public/auth/forgot-password`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    }
  )

  if (!response.ok) {
    const errorBody = await response.json()
    throw new Error(errorBody.message || 'Failed to send reset email')
  }
}

export const resetPassword = async (body: {
  email: string
  code: string
  newPassword: string
}): Promise<void> => {
  const response = await fetch(
    `${AWS_API_URL}/dashboard/v1/public/auth/reset-password`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }
  )

  if (!response.ok) {
    const errorBody = await response.json()
    throw new Error(errorBody.message || 'Failed to reset password')
  }
}

// TODO: connect when the backend endpoint exists
export const setPassword = async (body: {
  email: string
  password: string
}): Promise<void> => {
  const response = await fetch(
    `${AWS_API_URL}/dashboard/v1/public/auth/set-password`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }
  )

  if (!response.ok) {
    const errorBody = await response.json()
    throw new Error(errorBody.message || 'Failed to set password')
  }
}

export const validateInviteToken = async (
  inviteToken: string,
  email?: string,
): Promise<{ valid: boolean; email: string }> => {
  const params = new URLSearchParams({ invite_token: inviteToken })
  if (email) params.append('email', email)

  const response = await fetch(
    `${AWS_API_URL}/dashboard/v1/public/auth/setup/validate?${params}`,
  )

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}))
    throw new Error(errorBody.message || 'Invalid invite token')
  }

  const result = (await response.json()) as ResponseType<{ valid: boolean; email: string }>
  if (!result.success) throw new Error(result.message || 'Invalid invite token')
  return result.data
}

export const respondToChallenge = async (body: {
  challengeName: string
  session: string
  username: string
  code: string
}): Promise<BackendAuthResponse> => {
  const response = await fetch(
    `${AWS_API_URL}/dashboard/v1/public/auth/challenge`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }
  )

  if (!response.ok) {
    const errorBody = await response.json()
    throw new Error(errorBody.message || 'MFA verification failed')
  }

  const result = (await response.json()) as ResponseType<BackendAuthResponse>
  if (!result.success) throw new Error(result.message || 'MFA verification failed')
  return result.data
}

export const authSetup = async (body: {
  invite_token: string
  password: string
  email?: string
}): Promise<BackendAuthResponse> => {
  const response = await fetch(
    `${AWS_API_URL}/dashboard/v1/public/auth/setup`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }
  )

  if (!response.ok) {
    const errorBody = await response.json()
    throw new Error(errorBody.message || 'Account setup failed')
  }

  const result = (await response.json()) as ResponseType<BackendAuthResponse>
  if (!result.success) throw new Error(result.message || 'Account setup failed')
  return result.data
}
